import { spMarketplaces } from '@/@data/spMarketplaces';

export const mutationStatuses = ['pending', 'executed', 'failed', 'canceled'];

// Two spellings, permanently. Rows written from catalog revision 2 onward
// store `gated`; rows written before it keep `gated-write` forever, because
// the queue's immutable-snapshot trigger forbids rewriting a stored mode.
// A reviewer has to be offered both or every pre-cutover row silently loses
// its approve/reject buttons on the day MCP deploys.
export const isGated = (mode) => mode === 'gated' || mode === 'gated-write';

export function reviewReference(mutation, decision) {
  if (!['approve', 'reject'].includes(decision)) throw new Error('Invalid review decision');
  if (mutation.status !== 'pending' || !isGated(mutation.mode) ||
      mutation[`can_${decision}`] !== true || !Number.isInteger(mutation.lifecycle_version)) {
    throw new Error('This mutation is no longer available for review. Refresh the queue.');
  }
  return { id: mutation.id, expected_version: mutation.lifecycle_version };
}

export function mutationError(error) {
  if (error?.response?.status === 409) return 'This mutation changed while you were reviewing it. Refresh and review its current state.';
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : detail?.message || detail?.reason || error?.message || 'Unable to load mutations';
}

export function reviewResultMessage(result) {
  return `${result.id}: ${result.success === false ? (result.reason || result.error || 'Review failed') : (result.status || 'Decision recorded')}`;
}

// The operation ids the queue can carry, mirroring `_OPERATION_LIST` in
// wiseppc-mcp/src/wiseppc_mcp/permissions/nodes.py. `GET /app/v1/ai-actions/types`
// publishes the same list at runtime and is the authority; this map only
// supplies the human label, so a new id added upstream degrades to its raw
// form rather than breaking the grid.
export const OPERATION_LABELS = {
  'search_terms.track': 'Track search terms',
  'search_terms.untrack': 'Untrack search terms',
  'ads.campaigns.create': 'Create campaign',
  'ads.campaigns.update': 'Update campaign',
  'ads.campaigns.delete': 'Delete campaign',
  'ads.ad_groups.create': 'Create ad group',
  'ads.ad_groups.update': 'Update ad group',
  'ads.ad_groups.delete': 'Delete ad group',
  'ads.ads.create': 'Create ad',
  'ads.ads.update': 'Update ad',
  'ads.ads.delete': 'Delete ad',
  'ads.targets.create': 'Create target',
  'ads.targets.update': 'Update target',
  'ads.targets.delete': 'Delete target',
  'ads.portfolios.create': 'Create portfolio',
  'ads.portfolios.update': 'Update portfolio',
  'ads.rules.create': 'Create rule',
  'ads.rules.update': 'Update rule',
  'ads.rules.set_state': 'Change rule state',
  'ads.rules.attach': 'Attach rule',
  'ads.rules.detach': 'Detach rule',
  'ads.rules.delete': 'Delete rule',
  'ads.assets.create': 'Create creative asset',
  'ads.assets.version': 'New creative asset version',
  'sp.aplus.create': 'Create A+ content',
  'sp.aplus.update': 'Update A+ content',
  'sp.aplus.set_asins': 'Change A+ content ASINs',
  'sp.aplus.submit': 'Submit A+ content for approval',
  'sp.aplus.suspend': 'Suspend A+ content',
  'sp.fulfillment.cancel': 'Cancel fulfillment order',
  'sp.listings.put': 'Replace listing',
  'sp.listings.patch': 'Update listing',
  'sp.listings.delete': 'Delete listing',
  'ebay.listings.mutation': 'Edit eBay listing',
  'ebay.ads.mutation': 'Change eBay ads',
  'walmart.prices.mutation': 'Change Walmart price',
  'walmart.prices.feed': 'Change Walmart price',
  'walmart.items.feed': 'Edit Walmart item',
  'walmart.items.mutation': 'Retire Walmart item',
  'walmart.inventory.feed': 'Change Walmart stock',
  'walmart.inventory.mutation': 'Change Walmart stock',
  'walmart.lagtime.feed': 'Change Walmart lag time',
  'walmart.orders.mutation': 'Change Walmart order',
  'walmart.returns.mutation': 'Update Walmart return',
  'shopify.products.mutation': 'Edit Shopify product',
  'shopify.media.mutation': 'Change Shopify product pictures',
  'shopify.variants.mutation': 'Edit Shopify variant',
  'shopify.inventory.mutation': 'Change Shopify stock',
  'shopify.metafields.mutation': 'Change Shopify product fields',
  'shopify.collections.mutation': 'Edit Shopify collection',
  'shopify.discounts.mutation': 'Edit Shopify discount',
  'shopify.metaobjects.mutation': 'Edit Shopify custom data',
  'shopify.menus.mutation': 'Edit Shopify menu',
  'shopify.pages.mutation': 'Edit Shopify page',
  'shopify.blogs.mutation': 'Edit Shopify blog',
  'shopify.articles.mutation': 'Edit Shopify article',
  'sp.awd.inbound.create': 'Create AWD inbound order',
  'sp.awd.inbound.confirm': 'Confirm AWD inbound order',
  'sp.awd.inbound.cancel': 'Cancel AWD inbound order',
  'ads.brand_store.update_page_asins': 'Change products on Store page',
  'ads.brand_store.submit_publish': 'Publish Store page',
  'ebay.messages.mutation': 'Reply to buyer message',
  'shopify.orders.mutation': 'Update Shopify order',
  'shopify.returns.mutation': 'Update Shopify return',
  'tgacos.target_acos.set': 'Set target ACOS',
  'tgacos.target_acos.clear': 'Clear target ACOS',
  'tgacos.target_acos.delete_version': 'Delete target ACOS version',
  'tgacos.unit_economics.set': 'Set unit economics',
  'tgacos.unit_economics.clear': 'Clear unit economics',
  'tgacos.unit_economics.delete_version': 'Delete unit economics version',
};

// Legacy ClickHouse rows carry `action_type` instead of `operation`, in the
// pre-queue vocabulary. Only the values actually seen in mcp.ai_proposed_actions.
const LEGACY_ACTION_LABELS = {
  update_campaign: 'Update campaign',
  update_ad_group: 'Update ad group',
  update_target: 'Update target',
  update_keyword: 'Update keyword',
  create_negative_keyword: 'Add negative keyword',
  create_negative_target: 'Add negative target',
  update_campaign_budget: 'Update campaign budget',
  update_bid: 'Update bid',
};

const humanize = (value) => {
  const raw = String(value || '').replace(/[._]/g, ' ').trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
};

const joinTitleParts = (parts) => parts.length < 2 ? parts[0] || ''
  : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

const TITLE_CONTROL_FIELDS = new Set(['confirm', 'endingReason', 'listingModel']);

const fieldTitle = (items, nouns, fallback, editText = false) => {
  const fields = [...new Set(items.flatMap((item) => Object.keys(item.fields || {}))
    .filter((field) => !TITLE_CONTROL_FIELDS.has(field))
    .map((field) => nouns[field] || fallback))];
  if (!fields.length) fields.push(fallback);
  const verb = editText && fields.every((field) => ['title', 'description'].includes(field)) ? 'Edit' : 'Change';
  const shown = fields.length > 2 ? [...fields.slice(0, 2), `${fields.length - 2} more`] : fields;
  return `${verb} ${joinTitleParts(shown)}`;
};

const itemNoun = (type) => String(type || 'item').replaceAll('_', ' ');
const countedNoun = (noun, count) => count > 1 ? `${count} ${noun}s` : noun;
const fieldOperation = (operation) => !operation || ['revise', 'update'].includes(operation);

function joinOperationPhrases(phrases) {
  // Each phrase starts with its verb; only the first one is capitalised in the joined title.
  const shown = phrases.slice(0, 3).map((phrase, index) => (index ? phrase.charAt(0).toLowerCase() + phrase.slice(1) : phrase));
  if (phrases.length > 3) shown.push(`${phrases.length - 3} more changes`);
  return joinTitleParts(shown);
}

function groupedOperationTitle(items, titleOf) {
  const groups = new Map();
  items.forEach((item) => {
    const key = `${item.operation}:${item.entityType}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return joinOperationPhrases([...groups.values()].map(titleOf));
}

function ebayAdsTitle(items) {
  const groups = new Map();
  items.forEach((item) => {
    let operation = item.operation;
    if (operation === 'update_status' || operation === 'update') {
      const status = item.fields?.adStatus ?? item.fields?.status ?? item.fields?.keywordStatus
        ?? item.fields?.negativeKeywordStatus ?? item.fields?.adGroupStatus;
      operation = { PAUSED: 'pause', ACTIVE: 'resume', RUNNING: 'resume', ENDED: 'end', ARCHIVED: 'archive' }[status] || operation;
    }
    const rate = ['update', 'bid', 'update_bid'].includes(operation) && item.fields?.bidPercentage !== undefined;
    const verb = rate ? 'Change' : ({
      pause: 'Pause', resume: 'Resume', end: 'End', delete: 'Delete', create: 'Create',
      archive: 'Archive', clone: 'Copy', update: 'Change', update_bid: 'Change', bid: 'Change',
      update_status: 'Change', update_budget: 'Change', rename: 'Rename', update_dates: 'Change',
      update_bidding_strategy: 'Change', update_max_cpc: 'Change', update_ad_rate_strategy: 'Change',
    }[operation] || humanize(operation));
    const noun = rate ? 'ad rate' : itemNoun(item.entityType);
    const key = `${operation}:${item.entityType}:${rate}`;
    if (!groups.has(key)) groups.set(key, { verb, noun, rate, count: 0 });
    groups.get(key).count += 1;
  });
  return joinOperationPhrases([...groups.values()].map(({ verb, noun, count, rate }) =>
    `${verb} ${rate && count === 1 ? '1 ' : ''}${countedNoun(noun, count)}`));
}

function ebayListingFieldsTitle(items) {
  const title = fieldTitle(items, {
    price: 'price', listPrice: 'price', quantity: 'stock', quantityAvailable: 'stock',
    title: 'title', description: 'description', itemSpecifics: 'item specifics', pictureUrls: 'pictures',
    categoryId: 'category', policies: 'policies', variations: 'variations', conditionId: 'condition',
  }, 'listing', true);
  const types = new Set(items.map((item) => item.entityType));
  const noun = types.size > 1 ? 'items' : `${itemNoun(items[0].entityType || 'listing')}s`;
  return title + (items.length > 1 ? ` on ${items.length} ${noun}` : '');
}

const FIELD_ITEM_OPERATIONS = {
  'walmart.prices.mutation': ['update', 'promo'],
  'walmart.inventory.feed': ['bulk'],
  'walmart.lagtime.feed': ['bulk'],
  'walmart.items.feed': ['content', 'maintenance'],
  'shopify.media.mutation': ['add_media', 'restore'],
};

function itemOperationTitle(id, items) {
  const known = FIELD_ITEM_OPERATIONS[id];
  if (known && items.some((item) => item.operation && !known.includes(item.operation))) {
    return groupedOperationTitle(items, (group) => known.includes(group[0].operation)
      ? itemOperationTitle(id, group)
      : `${humanize(group[0].operation)} ${countedNoun(itemNoun(group[0].entityType), group.length)}`);
  }
  if (id === 'ebay.listings.mutation') {
    if (new Set(items.map((item) => item.operation)).size === 1
      && items.every((item) => fieldOperation(item.operation))) return ebayListingFieldsTitle(items);
    return groupedOperationTitle(items, (group) => {
      if (fieldOperation(group[0].operation)) return ebayListingFieldsTitle(group);
      return `${humanize(group[0].operation)} ${countedNoun(itemNoun(group[0].entityType || 'listing'), group.length)}`;
    });
  }
  if (id === 'ebay.ads.mutation') return ebayAdsTitle(items);
  if (id === 'walmart.items.mutation') return groupedOperationTitle(items, (group) =>
    `${humanize(group[0].operation || 'retire')} ${group.length === 1 ? 'Walmart item' : `${group.length} items`}`);
  if (id === 'walmart.orders.mutation') return groupedOperationTitle(items, (group) =>
    ({ cancel_lines: 'Cancel order lines', refund_lines: 'Refund order lines' }[group[0].operation]
      || `${humanize(group[0].operation)} order`));
  if (id === 'walmart.prices.mutation') return 'Change price';
  if (id === 'walmart.inventory.feed') return `Change stock${items.length > 1 ? ` on ${items.length} SKUs` : ''}`;
  if (id === 'walmart.lagtime.feed') return 'Change lag time';
  if (id === 'walmart.items.feed') return fieldTitle(items, {
    mainImageUrl: 'pictures', productSecondaryImageURL: 'pictures', productName: 'title',
    shortDescription: 'description', keyFeatures: 'description', Visible: 'status',
  }, 'item');
  if (id === 'shopify.products.mutation') {
    if (items.some((item) => item.operation && item.operation !== 'update')) return groupedOperationTitle(items, (group) =>
      !group[0].operation || group[0].operation === 'update' ? shopifyProductFieldsTitle(group)
        : `${humanize(group[0].operation)} ${countedNoun('product', group.length)}`);
    return shopifyProductFieldsTitle(items);
  }
  if (id === 'shopify.metafields.mutation') return groupedOperationTitle(items, (group) =>
    `${humanize(group[0].operation)} product fields`);
  if (id === 'shopify.media.mutation') return 'Change product pictures';
  return null;
}

function shopifyProductFieldsTitle(items) {
  // A status write is not an edit: ARCHIVED archives the product and DRAFT takes it off sale.
  const statusOf = (item) => String(item.fields?.status || '').toUpperCase();
  const archived = items.filter((item) => statusOf(item) === 'ARCHIVED').length;
  if (archived) return `Archive ${countedNoun('product', archived)}`;
  const drafts = items.filter((item) => statusOf(item) === 'DRAFT').length;
  if (drafts) return `Set ${countedNoun('product', drafts)} to draft`;
  if (items.some((item) => Object.hasOwn(item.fields || {}, 'descriptionHtml'))) return 'Edit product description';
  if (items.some((item) => Object.hasOwn(item.fields || {}, 'metafields'))) {
    const count = items.reduce((sum, item) => sum + Object.keys(item.fields?.metafields || {}).length, 0);
    return `Edit ${count} product ${count === 1 ? 'field' : 'fields'}`;
  }
  if (items.some((item) => Object.hasOwn(item.fields || {}, 'title'))) return 'Edit product title';
  return 'Edit product';
}

export function operationLabel(row) {
  const id = row?.operation || row?.action_type;
  if (!id) return 'Unknown operation';
  // A negation is queued as `ads.targets.create` too; the title says which one is made.
  if (id === 'ads.targets.create' && negativeTargetLabel(row)) return 'Create negative target';
  const items = (row?.request ?? row?.params)?.items;
  const item = items?.[0];
  if (Array.isArray(items) && items.length) {
    const valid = items.filter((entry) => entry && typeof entry === 'object');
    const title = valid.length ? itemOperationTitle(id, valid) : null;
    if (title) return title;
  }
  if (id === 'shopify.orders.mutation' && item?.entityType === 'order') {
    const label = { cancel: 'Cancel order', refund: 'Refund order' }[item.operation];
    if (label) return label;
  }
  if (id === 'shopify.returns.mutation' && item?.entityType === 'return') {
    const label = { create: 'Create return', approve: 'Approve return', decline: 'Decline return', process: 'Process return' }[item.operation];
    if (label) return label;
  }
  if (Array.isArray(items) && id.endsWith('.mutation')) {
    const valid = items.filter((entry) => entry && typeof entry === 'object' && entry.operation);
    if (valid.length) return groupedOperationTitle(valid, (group) =>
      `${humanize(group[0].operation)} ${countedNoun(itemNoun(group[0].entityType), group.length)}`);
  }
  return OPERATION_LABELS[id] || LEGACY_ACTION_LABELS[id] || humanize(id);
}

export function batchOperationLabel(group) {
  const labels = (group.rows || []).map(operationLabel);
  if (!labels.length) return operationLabel({ operation: group.operation });
  // Rows that do different things say each one; one static label could call an end an edit.
  const distinct = [...new Set(labels)];
  if (distinct.length <= 3) return distinct.join(' · ');
  return `${distinct.slice(0, 2).join(' · ')} · and ${distinct.length - 2} more`;
}

// `reason` is open-ended on purpose — adapters surface their own QueueError
// codes through it. Known codes get a sentence; anything else is humanized,
// and the raw code is kept alongside for the tooltip.
export const REASON_LABELS = {
  aplus_review_rejected: 'Amazon rejected the A+ content review',
  aplus_document_not_found: 'The A+ document was not found',
  aplus_document_gone: 'The A+ document no longer exists',
  aplus_document_body_unavailable: 'The A+ document body could not be read',
  aplus_document_in_review: 'The A+ document is already in review',
  aplus_submit_requires_draft_or_rejected: 'Only draft or rejected A+ content can be submitted',
  aplus_asin_set_empty: 'At least one ASIN is required',
  aplus_asin_set_too_large: 'Too many ASINs were requested',
  aplus_asin_set_not_observed: 'The current A+ ASINs could not be confirmed',
  aplus_name_empty: 'The A+ document needs a name',
  aplus_create_name_must_embed_mutation_id: 'The A+ document name must include the mutation ID',
  aplus_before_state_cannot_round_trip: 'The current A+ document cannot be safely restored',
  aplus_document_too_large: 'The A+ document is too large',
  aplus_endpoint_plane_changed: 'The A+ endpoint plane changed since review',
  aplus_endpoint_plane_unavailable: 'The A+ endpoint plane could not be confirmed',
  create_rejected: 'The create request was rejected',

  withdrawn: 'Withdrawn by its creator',
  replaced: 'Replaced by a corrected request',
  rejected: 'Rejected by a reviewer',
  review_expired: 'Expired before anyone reviewed it',
  send_expired: 'Approved too late to send',
  batch_stopped: 'Stopped with the rest of its batch',
  entity_updated_after_mutation: 'The entity changed after this was proposed',
  property_changed_since_approval: 'A property this change writes was changed outside WisePPC after approval — nothing was sent',
  changed_since_review: 'The live value changed after you opened this card. Approve again if the change is still right.',
  coverage_changed: 'The profiles this change covers changed after it was submitted; nothing was written. Submit it again.',
  entity_state_changed: 'It no longer matches what is on Amazon, so it was not authorized',
  authority_denied: 'The key lost permission for this operation',
  worker_stopped_after_dispatch: 'The worker stopped mid-send',
  worker_completion_missing: 'The worker never reported an outcome',
  execution_uncertain: 'Amazon’s outcome could not be confirmed',
  state_read_unavailable: 'The current state could not be read',
  projection_failed: 'The requested change could not be projected',
  upstream_outcome_unknown: 'Amazon did not confirm the outcome',
  upstream_not_executed: 'Amazon did not apply the change',
  upstream_rejected: 'Amazon rejected the change',
  invalid_executor_result: 'The executor returned an unusable result',
  executor_not_configured: 'No executor is configured for this operation',
  executor_transport_unavailable: 'The executor could not be reached',
  entity_updated_at_unavailable: 'Amazon did not report when the entity last changed',
  historical_record_not_executable: 'Historical record — kept for reference only',
  // Neutral: eBay writes these today, but none of them is about Amazon.
  partially_applied: 'Only part of the change was applied',
  entity_updated_since_review: 'The items changed since this was opened for review',
  ebay_write_limit: 'The daily budget of eBay changes was used up',
  ebay_mutation_not_saved: 'The eBay executor never saved the change — nothing reached eBay',
  listing_writes_disabled: 'eBay listing changes are switched off',
  // Walmart's write switches. Preparing a change fails with these while writes are off, so the
  // current values cannot be read; nothing has been sent and the row stays pending.
  walmart_writes_not_enabled: 'Walmart changes are switched off for now, so the current values could not be read. Nothing reached Walmart; the change is sent once they are switched on',
  mcp_writes_disabled: 'Walmart changes are switched off at Walmart in this environment. Nothing reached Walmart',
  feed_writes_disabled: 'Bulk (feed) changes are switched off for Walmart in this environment. Nothing reached Walmart',
  invalid_list_price: 'The list price must be a number above 0, with at most two decimal places.',
  list_price_with_quantity: 'A list price cannot be sent together with a quantity change.',
  list_price_confirm_required: 'A list price at or below the selling price needs confirmation, because eBay stores it and shows no strikethrough.',
};

// eBay's own wording for the reasons whose sentence names the marketplace.
// Same sense as MCP's `failures.EBAY_MESSAGES`; any other reason reads the same
// on every channel.
const EBAY_REASON_LABELS = {
  execution_uncertain: 'eBay’s outcome could not be confirmed',
  upstream_outcome_unknown: 'eBay did not confirm the outcome — it may have applied the change',
  upstream_not_executed: 'eBay did not apply the change',
  upstream_rejected: 'eBay rejected the change',
  partially_applied: 'eBay applied part of the change and rejected the rest',
};

const isEbayOperation = (row) => String(row?.operation || '').startsWith('ebay.');
const isTgacosOperation = (row) => String(row?.operation || '').startsWith('tgacos.');

// Target settings keeps the shared coverage sentence and uses its own wording
// for a value that moved after approval. Other operations keep the Amazon sentence.
const TGACOS_REASON_LABELS = {
  property_changed_since_approval: 'The value changed after approval; nothing was written.',
};

const SEARCH_TERMS_REASON_LABELS = {
  cap_reached: 'Over the 500 tracked-term limit. Nothing changed.',
};

const UPSTREAM_BY_PREFIX = { ebay: 'eBay', shopify: 'Shopify', walmart: 'Walmart' };

/**
 * The marketplace a row's change goes to, as the sentences about it name it.
 * Everything that is not eBay, Shopify or Walmart keeps reading "Amazon", exactly as it did.
 */
export const upstreamName = (row) => UPSTREAM_BY_PREFIX[String(row?.operation || '').split('.')[0]] || 'Amazon';

/** `row` is optional: without it every reason reads as it always has. */
export const reasonLabel = (reason, row = null) => {
  if (!reason) return '';
  if (String(row?.operation || '').startsWith('search_terms.') && SEARCH_TERMS_REASON_LABELS[reason]) return SEARCH_TERMS_REASON_LABELS[reason];
  if (isEbayOperation(row) && EBAY_REASON_LABELS[reason]) return EBAY_REASON_LABELS[reason];
  if (isTgacosOperation(row) && TGACOS_REASON_LABELS[reason]) return TGACOS_REASON_LABELS[reason];
  return REASON_LABELS[reason] || humanize(reason);
};

// status → chip. `type` maps onto el-tag's palette; `tone` drives our own
// token-based colouring where el-tag's is too loud.
export const STATUS_META = {
  pending: { label: 'Pending', type: 'warning' },
  executed: { label: 'Executed', type: 'success' },
  failed: { label: 'Failed', type: 'danger' },
  canceled: { label: 'Canceled', type: 'info' },
  // Legacy ClickHouse vocabulary. Note the double-l `cancelled` — the queue
  // spells it with one l, the frozen history table with two, and both reach
  // this map through the same grid.
  approved: { label: 'Approved', type: 'success' },
  declined: { label: 'Declined', type: 'danger' },
  cancelled: { label: 'Cancelled', type: 'info' },
  executing: { label: 'Executing', type: 'primary' },
};

export const statusMeta = (status) => STATUS_META[status] || { label: humanize(status) || 'Unknown', type: 'info' };

// phase is secondary to status: it says where in the lifecycle the row sits.
// `alert` marks the two phases a reviewer must not read as "nothing happened".
export const PHASE_META = {
  awaiting_approval: { label: 'Awaiting approval' },
  ready: { label: 'Ready to send' },
  preparing: { label: 'Preparing' },
  executing: { label: 'Sending' },
  outcome_unknown: { label: 'Outcome unknown', alert: true },
  upstream_pending: { label: 'Waiting on Amazon', alert: true },
  terminal: { label: 'Complete' },
};

/** `row` is optional: without it every phase reads as it always has. */
const WAITING_ON = { eBay: 'Waiting on eBay', Shopify: 'Waiting on Shopify', Walmart: 'Waiting on Walmart' };
const FAILED_AT = { eBay: 'Failed at eBay', Shopify: 'Failed at Shopify', Walmart: 'Failed at Walmart' };

export const phaseMeta = (phase, row = null) => {
  const waiting = phase === 'upstream_pending' && row ? WAITING_ON[upstreamName(row)] : null;
  if (waiting) return { ...PHASE_META.upstream_pending, label: waiting };
  return PHASE_META[phase] || { label: humanize(phase) };
};

export const MODE_META = {
  gated: { label: 'Gated', hint: 'Requires authorization before it is sent' },
  direct: { label: 'Direct', hint: 'Executes automatically, without review' },
};

export const modeMeta = (mode) => (isGated(mode)
  ? MODE_META.gated
  : MODE_META[mode] || { label: humanize(mode) || 'Unknown', hint: '' });

export const REVERSAL_LABELS = {
  reversible: 'Reversible',
  compensating: 'Undone by a compensating change',
  restore_previous: 'Previous values can be restored',
  conditional: 'Conditionally reversible',
  not_reversible: 'Cannot be undone',
};

export const reversalLabel = (value) => (value ? REVERSAL_LABELS[value] || humanize(value) : '');

/**
 * Queue rows and legacy history arrive from separate app API history endpoints.
 * They use different field names and status vocabularies, so renderers need
 * to know which kind of row they hold.
 */
export const isLegacyRow = (row) => row?.status_contract === 'legacy'
  || row?.source === 'legacy_ai_proposed_actions'
  || row?.read_only === true;

// One target per snapshot — `normalize()` enforces one entity per mutation,
// so `targets[0]` is the target, not a sample of many.
export function primaryTarget(row) {
  const target = Array.isArray(row?.targets) ? row.targets[0] : null;
  if (target) return target;
  // Legacy rows carry the entity flat instead of in a targets array.
  if (row?.entity_type) {
    return { provider: 'ads', entity_type: row.entity_type, entity_id: row.entity_id ? String(row.entity_id) : null };
  }
  return null;
}

export const ENTITY_TYPE_LABELS = {
  campaign: 'Campaign',
  ad_group: 'Ad group',
  ad: 'Ad',
  target: 'Target',
  // A queued row says `target` even for a negation — Amazon deletes a negative
  // keyword through the same endpoint. `target_details` reports the type the
  // lookup RESOLVED to, so `negative` only ever arrives from there.
  negative: 'Negative',
  keyword: 'Keyword',
  portfolio: 'Portfolio',
  rule: 'Rule',
  creative_asset: 'Creative asset',
  listing: 'Listing',
  fulfillment_order: 'Fulfillment order',
  conversation: 'Conversation',
  sku: 'SKU',
  order: 'Order',
  return: 'Return',
  target_settings: 'Target settings',
};

export const entityTypeLabel = (type) => ENTITY_TYPE_LABELS[type] || humanize(type) || 'Entity';

/**
 * The `target_details` element describing `primaryTarget(row)`.
 *
 * The server aligns `target_details` positionally with `targets` rather than
 * keying it by entity id, because a create has no id to key on. `normalize()`
 * allows exactly one entity per mutation, so index 0 is the target — but the
 * array is absent on legacy rows and on any response written before the
 * enrichment shipped, and its element is null whenever the entity could not be
 * resolved (a create, an archived entity, or anything off the ads plane).
 */
export function targetDetails(row) {
  const details = Array.isArray(row?.target_details) ? row.target_details[0] : null;
  return details || null;
}

const requestOfRow = (row) => row?.request ?? row?.params;

// The Amazon Ads entity a queued `ads.*` request carries (`normalize()` stores one).
export function adsRequestEntity(row) {
  if (!String(row?.operation || '').startsWith('ads.')) return null;
  const entity = requestOfRow(row)?.entities?.[0];
  return entity && typeof entity === 'object' ? entity : null;
}

const adsIsCreate = (row) => /^ads\.[a-z_]+\.create$/.test(String(row?.operation || ''));

/**
 * What Amazon answered for an `ads.*.create` that was sent: the id it gave the
 * new entity and its name (`result.results[].entity_id` / `amazon_response.name`),
 * or null before the send, for a failure, and for any other operation.
 */
export function adsCreatedResult(row) {
  if (!adsIsCreate(row)) return null;
  const results = row?.result?.results;
  const entry = Array.isArray(results) ? results.find((item) => item && item.success === true) : null;
  if (!entry) return null;
  const id = entry.entity_id && entry.entity_id !== 'unknown' ? String(entry.entity_id) : '';
  const name = typeof entry.amazon_response?.name === 'string' ? entry.amazon_response.name : '';
  return id || name ? { id, name } : null;
}

const TARGET_KINDS = {
  keywordTarget: 'keyword',
  productTarget: 'product',
  productCategoryTarget: 'category',
  productAudienceTarget: 'audience',
  themeTarget: 'theme',
};
const TARGET_TYPE_KINDS = {
  KEYWORD: 'keyword',
  PRODUCT: 'product',
  PRODUCT_CATEGORY: 'category',
  PRODUCT_AUDIENCE: 'audience',
  THEME: 'theme',
};

/**
 * "Negative keyword" / "Negative product" for a negation, else ''. A queued
 * `target` is declared a target even when it negates (see `ENTITY_TYPE_LABELS`),
 * so the request's own `negative` flag counts as well as the type the lookup resolved.
 */
export function negativeTargetLabel(row) {
  const request = adsRequestEntity(row);
  const resolved = targetDetails(row);
  if (targetEntityType(row) !== 'negative' && request?.negative !== true) return '';
  const fromBody = Object.keys(request?.targetDetails || {}).map((key) => TARGET_KINDS[key]).find(Boolean);
  const kind = fromBody
    || TARGET_TYPE_KINDS[request?.targetType]
    || TARGET_TYPE_KINDS[resolved?.details?.target_type]
    || '';
  if (kind) return `Negative ${kind}`;
  return request?.negative === true ? 'Negative target' : '';
}

/**
 * What the row acts on, preferring the type the lookup resolved over the one
 * the proposer declared: a queued `target` that came back a negation must read
 * "Negative", not "Target".
 */
export function targetEntityType(row) {
  return targetDetails(row)?.entity_type || primaryTarget(row)?.entity_type || '';
}

/**
 * A human name for what the row acts on, or null.
 *
 * Degradation chain, richest first: the server's entity lookup, the legacy flat
 * `entity_name` column, then the snapshot the proposer wrote at submit time.
 *
 * It deliberately stops at null rather than falling back to the Amazon id — an
 * id is not a name, and showing one on the name line tells a reviewer they know
 * which campaign they are approving when they do not. The caller renders the
 * type plus the id instead, which is the honest version.
 */
export function targetName(row) {
  const detail = targetDetails(row);
  if (detail?.name) return String(detail.name);
  if (row?.entity_name) return String(row.entity_name);
  // A create has no entity to look up: once sent, Amazon's answer names it; before
  // that, the name it asks for is the best there is.
  const created = adsCreatedResult(row);
  if (created?.name) return created.name;
  const proposed = adsIsCreate(row) ? adsRequestEntity(row)?.name : null;
  if (proposed && typeof proposed === 'string') return proposed;
  const snapshot = row?.metadata?.contextSnapshot
    || row?.metadata?.context_snapshot
    || row?.context_snapshot;
  // Keyed on the type the PROPOSER declared, which is not always the one the
  // lookup resolved to, so both are tried.
  const keys = [primaryTarget(row)?.entity_type, detail?.entity_type];
  for (const key of keys) {
    const value = key ? snapshot?.[`${key}_name`] : null;
    if (value) return String(value);
  }
  return null;
}

// Whether a `target_details` element carries enough for the olapV2 renderer to
// draw anything. The renderers self-hide on an empty display value (`showCell`
// in Target.vue / AdCreativeProduct.vue, `isVisible` in Campaign.vue et al), so
// without this the drawer would swap the honest type+id block for a blank box.
// One predicate, shared by the component and by the caller deciding to mount it.
const ENTITY_CARD_PRECONDITION = {
  campaign: (d) => !!d.details.name,
  ad_group: (d) => !!d.details.name,
  portfolio: (d) => !!d.details.name,
  // The renderer only swaps the ASIN in for the name on a PRODUCT target.
  target: (d) => !!(d.details.name || (d.details.target_type === 'PRODUCT' && d.details.product_asin)),
  negative: (d) => !!d.details.name,
  ad: (d) => !!(d.details.creative_asin || d.details.creative_sku || d.details.name
    || d.product?.brand || d.product?.image_url),
};

export const hasEntityCard = (detail) => {
  const precondition = ENTITY_CARD_PRECONDITION[detail?.entity_type];
  return !!precondition && !!detail?.details && precondition(detail);
};

/**
 * The ancestor breadcrumb for one `target_details` element, farthest-first
 * (`Campaign X > Ad group Y`) — the server emits it nearest-first.
 *
 * Ancestors carry `{id, name}` only, except on a create, where the first node is
 * the parent's FULL details because it is the only context a row with no entity
 * of its own has. A portfolio ancestor can arrive with a name and no id at all,
 * so nothing here may assume one.
 */
export function entityChain(detail) {
  const chain = Array.isArray(detail?.entity_chain) ? detail.entity_chain : [];
  return chain
    .filter((node) => node?.type && node.details)
    .map((node) => {
      const id = node.details[`${node.type}_id`];
      return {
        type: node.type,
        label: entityTypeLabel(node.type),
        name: node.details.name ? String(node.details.name) : '',
        // Same rule as `targetName`: an id never stands in for a name. The
        // last-4 hint says "this one" without pretending to be what it is called.
        hint: !node.details.name && id ? `(…${String(id).slice(-4)})` : '',
      };
    })
    .filter((crumb) => crumb.name || crumb.hint)
    .reverse();
}

/**
 * Queue rows timestamp in ISO-8601 with a zone; the frozen ClickHouse rows
 * store `2026-04-08 06:33:12.000` — written as UTC but with no marker saying
 * so, which JS parses as local time. Both vocabularies reach the same cells,
 * so every timestamp goes through here first. Same fix as `_parseUtc` in
 * ai-actions/CommentThread.vue.
 */
export function parseMutationDate(raw) {
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  let value = String(raw).trim();
  if (!value.includes('T') && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(value)) {
    value = `${value.replace(' ', 'T')}Z`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The agent's short reason for the change (plain text, at most 280 characters, cut with "…").
 *
 * The queue serializes it as a top-level `rationale`; `metadata.rationale` is the same text as the
 * caller sent it (the legacy propose path writes `{rationale, contextSnapshot, legacy_action_type}`),
 * so it only answers for a payload the top-level field is missing from. The frozen ClickHouse rows
 * carry it as a top-level column too.
 */
export function rationaleOf(row) {
  const value = row?.rationale ?? row?.metadata?.rationale ?? '';
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The agent's longer markdown explanation, or ''. A list page answers `{omitted: true, chars: N}`
 * in its place; that marker is not text, so it reads as nothing until the single-row read lands.
 */
export function rationaleDetailsOf(row) {
  const value = typeof row?.rationale_details === 'string'
    ? row.rationale_details
    : row?.metadata?.rationale_details;
  return typeof value === 'string' ? value.trim() : '';
}

const normalizeReasonText = (text) => text.replace(/\s+/g, ' ').trim();

/**
 * How the agent's reason and details are laid out in the detail panel.
 *
 * The backend cuts a stored reason longer than 280 characters into `rationale` (ending in "…") and
 * puts the whole reason, then any details the agent sent, into `rationale_details`. Showing both
 * would print the same text twice. When the details already open with the reason (whitespace
 * normalised, minus the trailing "…"), the details markdown IS the reason: `whyMarkdown` carries it
 * and `summary` / `detailsMarkdown` stay empty. Otherwise the short reason stays as plain text in
 * `summary` and the details follow under their own heading in `detailsMarkdown`.
 */
export function rationaleBlocks(row) {
  const summary = rationaleOf(row);
  const details = rationaleDetailsOf(row);
  if (!details) return { summary, whyMarkdown: '', detailsMarkdown: '' };
  const head = normalizeReasonText(summary).replace(/(…|\.{3})$/, '').trim();
  if (head && normalizeReasonText(details).startsWith(head)) {
    return { summary: '', whyMarkdown: details, detailsMarkdown: '' };
  }
  return { summary, whyMarkdown: '', detailsMarkdown: details };
}

/**
 * Who asked for this change.
 *
 * `present()` adds no key name, so the queue only ever gives us
 * `principal` (`key:52` / `wiseppc:user:3`) plus `creator_key_id`. The name
 * has to be resolved separately against the API-key list; `keyNames` is that
 * lookup when the caller has one.
 */
export function agentIdentity(row, keyNames = null) {
  if (!row) return null;
  // The MCP's own record of the caller. All of it can be null (old rows, a server that predates it).
  const agentName = textOf(row.agent_name);
  const clientLabel = [textOf(row.client_name), textOf(row.client_version)].filter(Boolean).join(' ');
  // Transport identifies REST calls independently of the client type guessed from the User-Agent.
  const extra = {
    agentName,
    clientLabel,
    clientType: textOf(row.client_type),
    userAgent: textOf(row.client_user_agent),
    transport: transportOf(row.transport),
  };
  // Legacy rows name the tool directly, which beats any id we could show.
  const legacyClientType = row.created_by_client_type;
  if (legacyClientType) {
    return {
      kind: 'client',
      ...extra,
      clientType: legacyClientType,
      userAgent: row.created_by_user_agent || '',
      label: '',
    };
  }
  const principal = String(row.principal || '');
  const keyId = row.creator_key_id ?? (principal.startsWith('key:') ? Number(principal.slice(4)) : null);
  if (keyId) {
    const named = keyNames?.get?.(Number(keyId));
    // A bare key id means nothing to a reviewer, so it never becomes the
    // visible label — it stays in the tooltip, where it is still the thing
    // you would quote in a support ticket.
    return { kind: 'key', keyId: Number(keyId), label: named || 'API key', named: !!named, ...extra };
  }
  if (principal.includes(':user:')) return { kind: 'user', label: 'User', userId: row.creator_user_id || null, ...extra };
  if (agentName) return { kind: 'agent', label: agentName, ...extra };
  if (extra.transport === 'rest') return { kind: 'client', label: '', ...extra };
  return null;
}

function textOf(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function transportOf(value) {
  if (value === 'api') return 'rest';
  return value === 'mcp' ? 'mcp' : '';
}

/**
 * The identity fields the MCP stores on each request, for the detail panel. `client_user_agent` is
 * only on the single-row read, so `detail` (the fetched record) is consulted per field. Every
 * field is '' when it is missing; `any` is false when there is nothing to show.
 */
export function requestedByOf(row, detail = null) {
  const pick = (key) => textOf(detail?.[key]) || textOf(row?.[key]);
  const fields = {
    agentName: pick('agent_name'),
    client: [pick('client_name'), pick('client_version')].filter(Boolean).join(' '),
    clientType: pick('client_type'),
    transport: transportOf(pick('transport')),
    session: pick('session_ref'),
    userAgent: pick('client_user_agent'),
  };
  return { ...fields, any: Object.values(fields).some(Boolean) };
}

/** The four identity filters the server accepts on `GET /app/v1/ai-actions`; each is also the row key it matches. */
export const IDENTITY_FILTER_KEYS = ['agent_name', 'client_name', 'client_type', 'session_ref'];

/** Only the identity filters that hold a value, as request params. */
export function identityParams(filters) {
  const params = {};
  IDENTITY_FILTER_KEYS.forEach((key) => {
    const value = textOf(filters?.[key]);
    if (value) params[key] = value;
  });
  return params;
}

/**
 * Safety net for a server that ignores the identity params: keep only rows whose field EXACTLY
 * equals each active filter value. `ignored` is true when the server returned rows and none
 * survived, which is how an unfiltered answer looks from here.
 */
export function applyIdentityFilter(items, params) {
  const keys = Object.keys(params || {});
  const list = Array.isArray(items) ? items : [];
  if (!keys.length) return { items: list, ignored: false };
  const kept = list.filter((row) => keys.every((key) => row?.[key] === params[key]));
  return { items: kept, ignored: list.length > 0 && kept.length === 0 };
}

/**
 * Name the actor behind one `events[]` entry.
 *
 * `actor` is either a principal string (`key:32`, `wiseppc:user:3`) or one of
 * the backend's own workers. Same rule as `agentIdentity`: a key is named, or
 * called "API key" — the id is never the visible label.
 */
export function actorLabel(actor, keyNames = null) {
  const value = String(actor || '');
  if (!value) return '';
  if (value === 'worker') return 'Worker';
  if (value === 'reconciler') return 'Reconciler';
  if (value === 'state_reader') return 'State reader';
  // The deploy-time backfill that dismissed failures older than a week.
  if (value === 'system:backfill') return 'Automatic cleanup';
  if (value.startsWith('key:')) {
    return keyNames?.get?.(Number(value.slice(4))) || 'API key';
  }
  if (value.includes(':user:')) return 'User';
  return value;
}

const USER_PRINCIPAL = /(?:^|:)user:(\d+)$/;

/** True when `principal` (`wiseppc:user:2`) is the logged-in person. */
export function isCurrentUserPrincipal(principal, currentUserId) {
  if (currentUserId == null) return false;
  const match = USER_PRINCIPAL.exec(String(principal || ''));
  return Boolean(match) && Number(match[1]) === Number(currentUserId);
}

/**
 * One `events[]` entry's actor as the Activity timeline names it: the logged-in
 * person is "You", otherwise the backend's `actor_name` (user display name / key
 * name), otherwise what `actorLabel` can say from the principal alone.
 */
export function eventActorLabel(event, { keyNames = null, currentUserId = null } = {}) {
  if (isCurrentUserPrincipal(event?.actor, currentUserId)) return 'You';
  const name = String(event?.actor_name || '').trim();
  return name || actorLabel(event?.actor, keyNames);
}

// Events that end a request without sending it, and the `reason` each one stands for.
const STOP_EVENTS = {
  cancel: 'withdrawn',
  reject: 'rejected',
  expired: 'review_expired',
  replace: 'replaced',
};
const SYSTEM_ACTIONS = new Set(['review_expired', 'send_expired', 'expired', 'replaced', 'batch_stopped']);
const SYSTEM_PRINCIPALS = new Set(['worker', 'reconciler', 'state_reader']);

function principalKind(principal) {
  const value = String(principal || '');
  if (!value) return null;
  if (value.startsWith('wiseppc:')) return 'user';
  if (SYSTEM_PRINCIPALS.has(value) || value.startsWith('system')) return 'system';
  return 'agent';
}

/** The text of a note, unless it is just a reason code repeated. */
function noteText(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  return text && !REASON_LABELS[text] ? text : null;
}

/** The last event that stopped the request, or null. */
function stopEventOf(events) {
  if (!Array.isArray(events)) return null;
  for (let index = events.length - 1; index >= 0; index -= 1) {
    if (STOP_EVENTS[events[index]?.event]) return events[index];
  }
  return null;
}

// The id of the request that took this one's place: the row's `revised_by` / `replaced_by` (on
// list rows too), else the `replace` event's `replacement_id`. '' when none does.
function replacedByOf(row, event) {
  const id = revisedByOf(row) || row.replaced_by
    || (event?.event === 'replace' ? event.data?.replacement_id : '');
  return id ? String(id) : '';
}

// The stop actions MutationStoppedBy words itself, so the outcome tag's own sentence
// ("Withdrawn by its creator") would only say it twice.
const STOP_LINE_ACTIONS = new Set([
  'rejected', 'withdrawn', 'canceled', 'review_expired', 'expired', 'send_expired', 'replaced', 'batch_stopped',
]);

/** Whether the stopped-by line already says what the outcome tag's detail sentence says. */
export const stopLineCoversOutcome = (stop) => Boolean(stop && STOP_LINE_ACTIONS.has(stop.action));

/**
 * Who stopped a canceled request, and why. The one place that knows the shapes:
 *
 * 1. the row's `stopped_by` ({kind, action, principal, name, at, note}) when the
 *    server sends it;
 * 2. else the row's own fields (`reason`, `creator_key_id`, `replaced_by`, `revised_by`)
 *    plus, when the loaded record's `events` are passed, the terminal cancel / reject
 *    event's actor and `data.reason`.
 *
 * `who` is an English phrase for the actor ("you", "Maksym Tsanko", "the agent (Claude
 * Code)", "a team member"; "" for the system). `name` is the raw display name, so a
 * template can keep it out of the translator. `you` is true for the logged-in person.
 * Null when the row was not stopped.
 */
export function stoppedByOf(row, { keyNames = null, currentUserId = null, events = null } = {}) {
  if (!row) return null;
  const stored = row.stopped_by && typeof row.stopped_by === 'object' ? row.stopped_by : null;
  const canceled = row.status === 'canceled' || row.status === 'cancelled';
  if (!stored && !canceled) return null;

  const event = stored ? null : stopEventOf(events || row.events);
  const reason = String(row.reason || '');

  let action = String(stored?.action || '');
  if (!action) {
    // Same rule as `mutationOutcome`: a reason a revision can stop with wins over `replaced_by`.
    if ((revisedByOf(row) || row.replaced_by) && !REVISION_STOPPED[reason]) action = 'replaced';
    else if (reason && reason !== 'historical_record_not_executable') action = reason;
    else if (event) action = STOP_EVENTS[event.event];
  }
  if (!action) return null;

  let principal = String(stored?.principal || event?.actor || '');
  if (!principal && action === 'withdrawn' && row.creator_key_id) principal = `key:${row.creator_key_id}`;
  let kind = stored?.kind || principalKind(principal);
  if (!kind) {
    if (action === 'rejected') kind = 'user';
    else if (action === 'withdrawn') kind = 'agent';
    else if (SYSTEM_ACTIONS.has(action)) kind = 'system';
  }
  if (!kind) return null;

  const you = kind === 'user' && isCurrentUserPrincipal(principal, currentUserId);
  let name = String(stored?.name || event?.actor_name || '').trim() || null;
  if (!name && principal.startsWith('key:')) name = keyNames?.get?.(Number(principal.slice(4))) || null;
  if (!name && !principal && kind === 'agent' && row.creator_key_id) {
    name = keyNames?.get?.(Number(row.creator_key_id)) || null;
  }

  let who = '';
  if (you) who = 'you';
  else if (kind === 'user') who = name || 'a team member';
  else if (kind === 'agent') who = name ? `the agent (${name})` : 'the agent';

  return {
    who,
    kind,
    action,
    replacedBy: action === 'replaced' ? replacedByOf(row, stopEventOf(events || row.events)) : '',
    at: parseMutationDate(stored?.at || event?.created_at || row.terminal_at || null),
    note: noteText(stored ? stored.note : event?.data?.reason),
    name: you ? null : name,
    you,
  };
}

// The first segment of an operation id is the connection it belongs to and the
// middle one its entity group. Both come straight out of the id, so a new
// operation published by `/mutations/types` slots in without a change here; a
// connection missing from this map is still grouped, under a humanized name.
// The map's order is the order the filter lists connections in.
const CONNECTION_LABELS = {
  ads: 'Amazon Ads',
  sp: 'Amazon Seller',
  ebay: 'eBay',
  shopify: 'Shopify',
  walmart: 'Walmart',
  tgacos: 'Target settings',
};
const CONNECTION_ORDER = Object.keys(CONNECTION_LABELS);
const GROUP_LABELS = {
  campaigns: 'Campaigns',
  ad_groups: 'Ad groups',
  ads: 'Ads',
  targets: 'Targets',
  portfolios: 'Portfolios',
  assets: 'Creative assets',
  listings: 'Listings',
  fulfillment: 'Fulfillment',
  target_acos: 'Target ACOS',
  unit_economics: 'Unit economics',
};
// A group name that means something else on one connection: `ebay.ads.*` is
// Promoted Listings, not the Amazon Ads "Ads" entity.
const CONNECTION_GROUP_LABELS = { ebay: { ads: 'Promoted Listings' } };
// eBay, Shopify and Walmart publish one generic `mutation` operation per entity
// group (each item's own `operation` field says what it does), and Walmart a
// bulk `feed` beside some of them. "Mutation" says nothing to a reviewer.
const GENERIC_ACTION_LABELS = { mutation: 'Changes', feed: 'Bulk feed' };

// `humanize` already turns `.`/`_` into spaces and sentence-cases the result,
// so a multi-word action segment (`set_state`) reads "Set state" instead of
// the raw "Set_state" a plain slice-and-capitalize would leave behind.
const actionLabel = (id) => {
  const action = id.split('.').slice(2).join('.');
  return GENERIC_ACTION_LABELS[action] || humanize(action) || humanize(id);
};
const groupLabel = (connection, group) => CONNECTION_GROUP_LABELS[connection]?.[group]
  || GROUP_LABELS[group] || humanize(group);
const connectionRank = (connection) => {
  const rank = CONNECTION_ORDER.indexOf(connection);
  return rank === -1 ? CONNECTION_ORDER.length : rank;
};

/**
 * Shape `/mutations/types` into the connection → group → operation tree the
 * filter renders. A group whose only operation is the generic `mutation` is a
 * leaf itself (eBay → Listings): a lone "Changes" child adds a click and
 * nothing else. Ids without three segments are kept at the top level rather
 * than dropped, so nothing becomes unfilterable.
 */
export function buildOperationTree(ids) {
  const connections = new Map();
  const loose = [];
  (ids || []).filter(Boolean).forEach((id) => {
    const [connection, group, action] = String(id).split('.');
    if (!connection || !group || !action) {
      loose.push({ value: id, label: operationLabel({ operation: id }) });
      return;
    }
    if (!connections.has(connection)) connections.set(connection, new Map());
    const groups = connections.get(connection);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push({ value: id, label: actionLabel(id) });
  });
  const tree = [...connections.entries()]
    .sort(([a], [b]) => connectionRank(a) - connectionRank(b))
    .map(([connection, groups]) => ({
      value: `connection:${connection}`,
      label: CONNECTION_LABELS[connection] || humanize(connection),
      children: [...groups.entries()].map(([group, children]) => {
        const label = groupLabel(connection, group);
        if (children.length === 1 && children[0].value === `${connection}.${group}.mutation`) {
          return { value: children[0].value, label };
        }
        return { value: `group:${connection}.${group}`, label, children };
      }),
    }));
  return loose.length ? [...tree, ...loose] : tree;
}

/** Only leaves are real operation ids; branch values are synthetic. */
export const isOperationId = (value) => typeof value === 'string'
  && !value.startsWith('connection:') && !value.startsWith('group:');

/**
 * Whether a row's operation falls under one of the operation filter's
 * selected values.
 *
 * `check-strictly` on the `el-tree-select` keeps a checked connection or
 * group as ONE chip rather than expanding it to every descendant leaf, so the
 * match has to be done hierarchically here instead of by exact membership: a
 * `connection:ads` chip covers every `ads.*` id, a `group:ads.rules` chip
 * covers every `ads.rules.*` id, and a leaf value matches only itself. The
 * trailing `.` on both prefix checks matters — without it `connection:ads`
 * would also catch a hypothetical `adsx.*` id, and a `group:ads.rule` value
 * would catch `ads.rules.*`.
 *
 * Legacy ClickHouse rows carry a dotless `action_type` (`update_campaign`)
 * with no group segment at all — `/mutations/types` never feeds them into
 * `buildOperationTree`, so a group or leaf chip can never reach one. But
 * every legacy row is an Ads mutation (`channelOf` treats them the same way),
 * so the top `connection:ads` branch is the one value they CAN satisfy.
 */
export function matchesOperationFilter(row, selected) {
  const id = String(row?.operation || row?.action_type || '');
  if (!id || !selected?.length) return false;
  return selected.some((value) => {
    if (value.startsWith('connection:')) {
      const connection = value.slice('connection:'.length);
      return id.startsWith(`${connection}.`) || (connection === 'ads' && isLegacyRow(row));
    }
    if (value.startsWith('group:')) return id.startsWith(`${value.slice('group:'.length)}.`);
    return id === value;
  });
}

/**
 * Which marketplace a request acts on.
 *
 * Ads targets key on `profile_id`, SP targets on `seller_id` / `marketplace_id`,
 * and the authorized `unit` carries the same identity for rows written after
 * the capability migration. The value is only ever used to group and filter,
 * so any stable string will do.
 */
export function marketplaceKeyOf(row) {
  const target = primaryTarget(row);
  // Same shape as `getMarketplaceUid` in helpers/marketplaceUnion.js, so a key
  // derived here matches the account store's leaves without a translation step.
  if (target?.profile_id) return `ads:${target.profile_id}`;
  if (target?.seller_id && target?.marketplace_id) return `sp:${target.seller_id}:${target.marketplace_id}`;
  if (row?.unit?.unit) {
    return row.unit.kind === 'amazon_sp'
      ? `sp:${row.unit.account}:${row.unit.unit}`
      : `ads:${row.unit.unit}`;
  }
  // Legacy rows carry the Ads profile directly.
  if (row?.amazon_ads_profile_id) return `ads:${row.amazon_ads_profile_id}`;
  return '';
}

/**
 * Every marketplace a row covers.
 *
 * A target-settings change is one row with one Ads target per profile. The
 * filter matches when any of those profiles is selected. A row with no target
 * key keeps the single `marketplaceKeyOf` result (the authorized unit, or a
 * legacy profile id). `profile_id` 0 is the business-profile scope, not a profile.
 */
export function marketplaceKeysOf(row) {
  const keys = [];
  const targets = Array.isArray(row?.targets) ? row.targets : [];
  targets.forEach((target) => {
    let key = '';
    if (target?.profile_id && Number(target.profile_id) !== 0) key = `ads:${target.profile_id}`;
    else if (target?.seller_id && target?.marketplace_id) {
      key = `sp:${target.seller_id}:${target.marketplace_id}`;
    }
    if (key && !keys.includes(key)) keys.push(key);
  });
  if (!keys.length) {
    const fallback = marketplaceKeyOf(row);
    if (fallback) keys.push(fallback);
  }
  return keys;
}

/** An empty selection matches every row. Otherwise any covered key is enough. */
export function matchesMarketplaceFilter(row, selected) {
  if (!selected?.length) return true;
  return marketplaceKeysOf(row).some((key) => selected.includes(key));
}

/**
 * Build the marketplace filter's options from the rows on hand, naming each
 * from the account store's marketplace leaves where it can be matched.
 * Deriving from rows rather than from every connected marketplace keeps the
 * filter to values that can actually match something — the queue is scoped to
 * one business profile but a row can name a marketplace the user no longer
 * has connected.
 */
export function marketplaceOptions(rows, marketplaceLeaves = []) {
  const names = new Map();
  (marketplaceLeaves || []).forEach((leaf) => {
    const uid = leaf?.marketplaceUid
      || (leaf?.profileId ? `ads:${leaf.profileId}` : null)
      || (leaf?.sellingPartnerId && leaf?.marketplaceStringId ? `sp:${leaf.sellingPartnerId}:${leaf.marketplaceStringId}` : null);
    if (!uid) return;
    const label = leaf.sellerName || leaf.accountName || leaf.name || '';
    const country = leaf.countryCode ? ` (${leaf.countryCode})` : '';
    if (label) names.set(uid, `${label}${country}`);
  });
  const seen = new Map();
  (rows || []).forEach((row) => {
    marketplaceKeysOf(row).forEach((key) => {
      if (!key || seen.has(key)) return;
      // Unresolvable keys still list, under their raw id — hiding them would
      // make their rows impossible to filter to.
      seen.set(key, { value: key, label: names.get(key) || key.split(':').slice(1).join(':') });
    });
  });
  return [...seen.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

// ---------------------------------------------------------------------------
// Decision-row vocabulary. Everything below feeds the review list rather than
// the old grid: one row has to say what changes, where, why and by when.
// ---------------------------------------------------------------------------

/**
 * Which system a request reaches. Derived from the operation's own prefix,
 * which is the only part of the vocabulary guaranteed present on every row —
 * `capability` is null on legacy rows and `unit` is null alongside it.
 */
export const CHANNEL_META = {
  // `icon` is the Iconify brand mark of the channel's group in the left navbar
  // (src/navigation/vertical/*.js), so a card and the menu show the same logo. Ads and Seller are
  // both Amazon. A channel without one (an unknown prefix) is drawn as a plain dot.
  ads: { key: 'ads', label: 'Amazon Ads', tone: 'violet', icon: 'selfhst:amazon' },
  sp: { key: 'sp', label: 'Amazon Seller', tone: 'orange', icon: 'selfhst:amazon' },
  shopify: { key: 'shopify', label: 'Shopify', tone: 'green', icon: 'selfhst:shopify' },
  ebay: { key: 'ebay', label: 'eBay', tone: 'blue', icon: 'selfhst:ebay' },
  walmart: { key: 'walmart', label: 'Walmart', tone: 'blue', icon: 'selfhst:walmart' },
  // Inert until the MCP door for Meta is on (WRITES_PLAN W3/W4): no queue row carries a `meta.*`
  // operation before that, and human inline edits never enter this queue.
  meta: { key: 'meta', label: 'Meta Ads', tone: 'blue', icon: 'logos:meta-icon' },
};

// The second line of the channel tag: which corner of that system. Keyed on
// the operation's middle segment, so a new `ads.budget_rules.*` degrades to a
// humanized "Budget rules" rather than disappearing.
const AREA_LABELS = {
  campaigns: 'Campaigns',
  ad_groups: 'Ad groups',
  ads: 'Ads',
  targets: 'Targeting',
  portfolios: 'Portfolios',
  negatives: 'Negative targeting',
  rules: 'Rules',
  listings: 'Listings',
  fulfillment: 'Fulfillment',
};

const TGACOS_AREAS = {
  target_acos: 'Target ACOS',
  unit_economics: 'Unit economics',
};

// Ads rows name their programme on the request, not the operation. Both
// spellings appear: the canonical submit path sends `ad_product`, the legacy
// translator sends `adProduct`.
const AD_PRODUCT_LABELS = {
  SPONSORED_PRODUCTS: 'Sponsored Products',
  SPONSORED_BRANDS: 'Sponsored Brands',
  SPONSORED_DISPLAY: 'Sponsored Display',
  SPONSORED_TV: 'Sponsored TV',
};

export function channelOf(row) {
  const id = String(row?.operation || row?.action_type || '');
  const [head, middle] = id.split('.');
  // Target settings is a WisePPC table reached through Amazon Ads profiles.
  const tgacos = head === 'tgacos';
  const channel = (tgacos ? CHANNEL_META.ads : CHANNEL_META[head])
    // Legacy rows carry `update_campaign`-style ids with no channel prefix at
    // all; every one of them is an Ads row.
    || (isLegacyRow(row) ? CHANNEL_META.ads : null)
    || { key: head || 'unknown', label: humanize(head) || 'Unknown', tone: 'grey' };
  const request = row?.request || null;
  const adProduct = request?.ad_product || request?.adProduct || null;
  const area = tgacos
    ? (TGACOS_AREAS[middle] || (middle ? humanize(middle) : ''))
    : ((channel.key === 'ads' && AD_PRODUCT_LABELS[adProduct])
      || AREA_LABELS[middle]
      || (middle ? humanize(middle) : ''));
  return { ...channel, area };
}

/**
 * Which marketplace a row acts on, resolved far enough to draw a flag.
 *
 * Prefers `row.marketplace`, the field the queue API grew for exactly this —
 * then resolves target / search-term preview marketplace ids against the catalog,
 * or joins the row's key against the account's marketplace leaves.
 *
 * 🔴 Never read `leaf.countryCode` for an ads leaf: an ads GB profile reports
 * "UK" while the SP catalog says "GB", and flags.css aliases `.flag-uk` onto
 * `.flag-gb`, so the mistake renders correctly and reads wrong. Go through
 * `marketplaceStringId` → the SP catalog, the same join marketplaceUnion.js
 * documents at its head.
 */
export function marketplaceOf(row, leafByUid = null) {
  const served = row?.marketplace;
  if (served) {
    if (served.scope === 'region') {
      const region = String(served.region || '').toUpperCase();
      return { scope: 'region', code: region, label: region ? `Multi-channel · ${region}` : 'Multi-channel', countryCode: null, currency: null, hasFlag: false, flagClass: '' };
    }
    const code = served.country_code || null;
    return {
      scope: 'marketplace',
      code,
      label: served.name || served.country_code || '',
      countryCode: code,
      currency: served.currency_code || null,
      hasFlag: Boolean(code),
      flagClass: code ? `flag flag-${String(code).toLowerCase()}` : '',
    };
  }

  const directMarketplaceId = row?.targets?.[0]?.marketplace_id || row?.search_terms_preview?.marketplace_id;
  const key = marketplaceKeyOf(row);
  if (!key && !directMarketplaceId) return { scope: 'unknown', code: '', label: '', countryCode: null, currency: null, hasFlag: false, flagClass: '' };

  // MCF names a region and genuinely has no marketplace. Say so rather than
  // picking one of the region's countries.
  const target = primaryTarget(row);
  if (!target?.marketplace_id && target?.region) {
    const region = String(target.region).toUpperCase();
    return { scope: 'region', code: region, label: `Multi-channel · ${region}`, countryCode: null, currency: null, hasFlag: false, flagClass: '' };
  }

  const marketplaceStringId = directMarketplaceId || (key.startsWith('sp:')
    ? key.split(':').slice(2).join(':')
    : leafByUid?.get?.(key)?.marketplaceStringId || null);
  const entry = marketplaceStringId
    ? spMarketplaces.find((m) => m.marketplaceId === marketplaceStringId)
    : null;

  if (!entry) {
    // An unresolved profile still has to be nameable, or its rows cannot be
    // told apart. It gets its raw id and no flag — never a guessed country.
    return {
      scope: 'unknown',
      code: '',
      label: directMarketplaceId || key.split(':').slice(1).join(':'),
      countryCode: null,
      currency: null,
      hasFlag: false,
      flagClass: '',
    };
  }
  return {
    scope: 'marketplace',
    code: entry.countryCode,
    label: entry.name,
    countryCode: entry.countryCode,
    currency: entry.currency,
    hasFlag: true,
    flagClass: `flag flag-${entry.countryCode.toLowerCase()}`,
  };
}

const SIX_HOURS = 6 * 60 * 60 * 1000;

const DISPATCHED_PHASES = new Set(['executing', 'upstream_pending', 'outcome_unknown']);

/**
 * Whether the request has already left for Amazon. `send_deadline` and
 * `expires_at` are both clocks on the time BEFORE sending, so on a dispatched
 * row they mean nothing — a sent-and-accepted row whose send window has since
 * closed is not "expired". `verification` is the server's own statement of
 * this; the claim stamps and phases cover a server that predates it.
 */
export function isDispatched(row) {
  return Boolean(row?.verification || row?.claimed_at || row?.attempt_id)
    || DISPATCHED_PHASES.has(row?.phase);
}

/** Sent and accepted, but not confirmed in time; the server keeps checking. */
export const isVerificationExpired = (row) => row?.verification?.state === 'expired';

/**
 * When a row stops being decidable. `send_deadline` is the tighter of the two
 * once a row is approved (`least(expires_at, approved_at + 15m)`), so it wins
 * where it exists.
 */
export function deadlineOf(row, now = Date.now()) {
  if (isDispatched(row)) return { at: null, msLeft: null, urgent: false, expired: false };
  const raw = row?.send_deadline || row?.expires_at;
  const at = parseMutationDate(raw);
  if (!at) return { at: null, msLeft: null, urgent: false, expired: false };
  const msLeft = at.getTime() - now;
  return { at, msLeft, urgent: msLeft > 0 && msLeft <= SIX_HOURS, expired: msLeft <= 0 };
}

/**
 * Whether `value` holds a list-page placeholder anywhere inside it. A list page replaces any string
 * over 2,000 characters with `{omitted: true, chars: N}`; a placeholder without a usable `chars`
 * still counts, because the text behind it has not been seen either way.
 */
export function hasOmittedText(value, depth = 0) {
  if (!value || typeof value !== 'object' || depth > 12) return false;
  if (!Array.isArray(value) && value.omitted === true) return true;
  return Object.values(value).some((entry) => hasOmittedText(entry, depth + 1));
}

// The row keys a list page trims (and the `before_state` inside `review_baseline`).
export const LIST_TRIMMED_KEYS = ['request', 'before_state'];

/**
 * Whether a row still carries a list-page placeholder in any trimmed field, so a reviewer would be
 * deciding on text they have not seen. Only the single-row read (`open`) replaces them.
 */
export const rowHasOmittedText = (row) => LIST_TRIMMED_KEYS.some((key) => hasOmittedText(row?.[key]))
  || hasOmittedText(row?.review_baseline?.before_state);

/**
 * Whether this row can still take this decision. The same three conditions
 * `reviewReference` throws on, asked without throwing — a row renders a status
 * instead of buttons, and only a reviewable row may be selected in bulk.
 */
export function reviewable(row, decision = 'approve') {
  return row?.status === 'pending'
    && isGated(row?.mode)
    && row?.[`can_${decision}`] === true
    && Number.isInteger(row?.lifecycle_version);
}

/**
 * The one sentence a reviewer needs from (status, phase, reason).
 *
 * The question every terminal row answers before any other is "did this reach
 * Amazon?" — "nothing was applied" and "sent, not confirmed" call for opposite
 * reactions, so they never share a tone or a verb.
 *
 * Unknown reasons still render: `reason` is free text with no database
 * constraint and about twenty known values, so the `failed_before_send`
 * default branch is load-bearing, not a fallback.
 */
export const OUTCOME_META = {
  pending: { label: 'Waiting for you', tone: 'warning', reached: false },
  // Approved and queued, but nothing has left yet — distinct from `in_flight`
  // precisely because `reached` differs, and `reached` is what decides whether
  // the UI may offer to send again.
  queued: { label: 'Queued to send', tone: 'info', reached: false },
  in_flight: { label: 'Sending', tone: 'info', reached: true },
  executed: { label: 'Applied', tone: 'success', reached: true },
  canceled: { label: 'Not sent', tone: 'muted', reached: false },
  replaced: { label: 'Replaced', tone: 'muted', reached: false },
  // A revision (a request that replaces a failed one) that was stopped before it
  // reached an executor. It is the end of its failure's lineage, so it can still
  // be open: reading it as a bare "Replaced"/"Canceled" would hide that.
  revision_rejected: { label: 'Revision rejected', tone: 'warning', reached: false },
  revision_withdrawn: { label: 'Revision withdrawn', tone: 'muted', reached: false },
  revision_expired: { label: 'Revision expired', tone: 'warning', reached: false },
  revision_replaced: { label: 'Revision replaced', tone: 'muted', reached: false },
  batch_stopped: { label: 'Batch stopped', tone: 'danger', reached: false },
  outcome_unknown: { label: 'Outcome unknown', tone: 'warning', reached: true },
  // Sent and accepted, but the effect was not confirmed in time. Amber, not
  // red: nothing failed, and the server keeps checking in the background.
  verification_expired: { label: 'Verification expired', tone: 'warning', reached: true },
  review_closed: { label: 'Review closed', tone: 'muted', reached: false },
  failed_at_amazon: { label: 'Failed at Amazon', tone: 'danger', reached: true },
  failed_before_send: { label: 'Could not be sent', tone: 'danger', reached: false },
};

const UNCONFIRMED_REASONS = new Set([
  'execution_uncertain',
  'upstream_outcome_unknown',
  'worker_completion_missing',
  'worker_stopped_after_dispatch',
]);

// Nothing was attempted in any of these: the review window shut, the entity
// moved underneath the review, or the key lost permission before the send.
// 🔴 `entity_updated_after_mutation` is deliberately NOT "Failed at Amazon" —
// the request never reached Amazon, so a headline saying it did would
// contradict the sentence printed beneath it.
// 🔴 `authority_denied` belongs here ONLY when nothing was sent. The worker
// writes it on both sides of dispatch (`"pending" if sent else "failed"`), and
// the sent case carries phase `outcome_unknown` — which is why the phase test
// below runs BEFORE this set is consulted. Reorder them and a request that may
// already have applied starts claiming it never left.
const CLOSED_REASONS = new Set([
  'review_expired',
  'send_expired',
  'authority_denied',
  'entity_updated_after_mutation',
]);

// Legacy statuses that mean the request was stopped before it was ever sent.
// Both spellings, because the frozen ClickHouse corpus uses British `cancelled`
// while the queue writes `canceled`.
const LEGACY_STOPPED = new Set(['cancelled', 'canceled', 'declined', 'rejected']);

// The only reasons that mean the marketplace itself saw the request and said
// no. `partially_applied` (eBay) is here because part of it DID apply: read as
// "Could not be sent" it would tell a reviewer nothing changed.
const AMAZON_REASONS = new Set(['upstream_rejected', 'upstream_not_executed', 'partially_applied']);

/**
 * A failure nobody has dealt with yet: the end of its lineage (not replaced by a
 * newer request) and not dismissed. These are the rows the Queue tab keeps
 * showing. Usually a failed row, but a revision that was rejected, withdrawn or
 * expired before it reached an executor is the end of the lineage too, so the
 * server flags it `open_failure` while its status stays `canceled`.
 *
 * A server that predates the flag sends no `open_failure`; then it is a failed
 * row that was neither revised nor dismissed, which is how the Queue behaved before.
 */
export function isOpenFailure(row) {
  if (typeof row?.open_failure === 'boolean') return row.open_failure;
  return row?.status === 'failed' && !row?.replaced_by && !row?.dismissed_at;
}

/**
 * Rows waiting for a decision, from the app API's `review` count.
 * Null when there is no snapshot or the count is not an integer.
 * The queue header and the header badge both read this so the two numbers match.
 */
export function reviewCount(counts) {
  return Number.isInteger(counts?.review) ? counts.review : null;
}

/**
 * Which queue tab holds this row.
 *
 * An open failure is Failed even when its status is `canceled` (a rejected
 * revision is still the failure to deal with), so that test is first. Anything
 * that is no longer `pending` is History. To review is the phase
 * `awaiting_approval` — `can_approve` is only set in that phase for a reviewer
 * who may approve, so it is not the key. A server that sends no phase falls
 * back to `reviewable`. Every other pending phase (ready, preparing, executing,
 * upstream_pending, outcome_unknown, an expired verification, a direct row) is
 * In progress.
 */
export function queueTabOf(row) {
  if (isOpenFailure(row)) return 'failed';
  if (row?.status !== 'pending') return 'history';
  if (row.phase === 'awaiting_approval' || (!row.phase && reviewable(row))) return 'review';
  return 'progress';
}

/**
 * Ids whose tab changed between two snapshots of the queue.
 * A row that was present and is missing from the next snapshot has left the
 * open list, which is History.
 * @returns {Map<string, string>}
 */
export function tabMoves(prevRows, nextRows) {
  const nextById = new Map();
  (nextRows || []).forEach((row) => {
    if (row?.id) nextById.set(row.id, row);
  });
  const moves = new Map();
  (prevRows || []).forEach((row) => {
    if (!row?.id) return;
    const to = nextById.has(row.id) ? queueTabOf(nextById.get(row.id)) : 'history';
    if (queueTabOf(row) !== to) moves.set(row.id, to);
  });
  return moves;
}

/**
 * Whether a row missing from every queue tab's page one has left for History.
 *
 * Pages are newest-first on `(created_at, id)`, the same order as the cursor.
 * A tab that returned `next_cursor` still has older rows, so a missing row of
 * that tab counts as gone only when it is newer than the last row on the page
 * — it would have been there. Anything older may sit further down and is
 * dropped with no departure. No cursor means that page was the whole tab. A
 * cursor with nothing to compare against (or a timestamp we cannot read)
 * cannot prove the row left.
 *
 * @param {object} row
 * @param {{ cursor?: string|null, last?: object|null }} bound page of the tab the row was on
 * @returns {boolean}
 */
export function missingRowDeparted(row, bound) {
  if (!bound?.cursor) return true;
  const last = bound.last;
  if (!last) return false;
  const rowTime = parseMutationDate(row?.created_at)?.getTime() ?? null;
  const lastTime = parseMutationDate(last?.created_at)?.getTime() ?? null;
  if (rowTime === null || lastTime === null) return false;
  if (rowTime !== lastTime) return rowTime > lastTime;
  // `ORDER BY id DESC`: a higher id is newer when the timestamps tie. Canonical
  // UUID text sorts the same way as the uuid type.
  return String(row?.id ?? '') > String(last?.id ?? '');
}

/**
 * The request that took over from this one, if any. On a failed row or on a
 * revision it is the newer revision; on any other canceled row `replaced_by` is
 * the old "corrected request" and has its own wording.
 */
export function revisedByOf(row) {
  if (row?.revised_by) return String(row.revised_by);
  return (row?.status === 'failed' || row?.revises) && row?.replaced_by ? String(row.replaced_by) : '';
}

const REVISION_STOPPED = {
  rejected: 'revision_rejected',
  withdrawn: 'revision_withdrawn',
  review_expired: 'revision_expired',
  send_expired: 'revision_expired',
  replaced: 'revision_replaced',
};

/** Outcome key of a canceled revision, from why it stopped; null when it is not one. */
function revisionStoppedKey(row) {
  if (!row?.revises || (row.status !== 'canceled' && row.status !== 'cancelled')) return null;
  if (row.replaced_by && !REVISION_STOPPED[row.reason]) return 'revision_replaced';
  return REVISION_STOPPED[row.reason] || null;
}

export function mutationOutcome(row) {
  const reason = row?.reason || (String(row?.operation || '').startsWith('search_terms.') ? row?.error_code : null);
  const status = row?.status;
  const phase = row?.phase;

  const key = (() => {
    const stopped = revisionStoppedKey(row);
    if (stopped) return stopped;
    // 🔴 A failed row that has been revised carries `replaced_by` too, but it is
    // still the failure — "Replaced" would erase the one fact its history
    // exists to keep. Only `store.change` (a canceled row) means replaced.
    if ((row?.replaced_by && status !== 'failed') || reason === 'replaced') return 'replaced';
    if (reason === 'batch_stopped') return 'batch_stopped';
    if (isVerificationExpired(row)) return 'verification_expired';
    if (phase === 'outcome_unknown' || UNCONFIRMED_REASONS.has(reason)) return 'outcome_unknown';
    if (CLOSED_REASONS.has(reason)) return 'review_closed';
    // 🔴 Frozen legacy records carry `historical_record_not_executable` on EVERY
    // row — `compat._project` writes it unconditionally. While that sat in
    // CLOSED_REASONS above, a legacy row that was approved, one declined and one
    // cancelled all collapsed into `review_closed`, so History's outcome chips
    // could not tell the legacy half apart at all.
    //
    // It is read here, on its own, rather than simply moved below the status
    // branches: `approved` in the legacy vocabulary meant *a person said yes*,
    // not that the write ever landed, so letting it fall through to the
    // `executed` branch would have a frozen row claim it reached Amazon on no
    // evidence. Only an explicit `executed` status or an `executed_at` stamp
    // says that. Anything else stays `review_closed`, which is the honest
    // answer for a record nobody can act on any more.
    if (reason === 'historical_record_not_executable') {
      if (LEGACY_STOPPED.has(status)) return 'canceled';
      if (status === 'executed' || row?.executed_at) return 'executed';
      return 'review_closed';
    }
    if (status === 'failed') return AMAZON_REASONS.has(reason) ? 'failed_at_amazon' : 'failed_before_send';
    if (status === 'executed' || status === 'approved') return 'executed';
    if (status === 'canceled' || status === 'cancelled' || status === 'declined') return 'canceled';
    // `ready` is approved and queued to dispatch — it is not waiting on a
    // person, so it must not read as though it were, and nothing has reached
    // Amazon yet, so it is not `in_flight` either.
    if (phase === 'ready') return 'queued';
    if (phase === 'preparing' || phase === 'executing' || phase === 'upstream_pending') return 'in_flight';
    return 'pending';
  })();

  const meta = OUTCOME_META[key];
  // The key stays `failed_at_amazon` for every channel — filters and icons are
  // keyed on it — and only the words change.
  const label = (key === 'failed_at_amazon' && FAILED_AT[upstreamName(row)]) || meta.label;
  // The server's own plain-language sentence, present only on a sent pending
  // row. Absent on an older server, where everything below is unchanged.
  const verification = row?.verification || null;
  const verifying = verification?.state === 'waiting' && key === 'in_flight';
  return {
    key,
    label: verifying ? `Waiting on ${upstreamName(row)}` : label,
    tone: meta.tone,
    reached: meta.reached,
    // Prefer the server's translated Amazon sentence where it has one; the
    // reason code alone cannot say *what* Amazon objected to.
    detail: verification?.message || row?.error_message || reasonLabel(reason, row),
    code: row?.error_code || reason || '',
  };
}

// Statuses where WisePPC's own copy of the listing is still behind what eBay accepted.
const SNAPSHOT_NOTE = new Set(['PENDING', 'GAVE_UP', 'FAILED']);

/**
 * What to say after a send, beyond the status chip.
 * `message` is the executor's sentence, and only on an executed row.
 * `snapshot` is PENDING, GAVE_UP or FAILED when that copy did not refresh — empty when the
 * sentence is already shown, and empty for DONE / NOT_NEEDED. The words live in the template
 * so `$t` keeps a string literal.
 */
export function mutationFollowUp(row) {
  const raw = row?.status === 'executed' && typeof row?.outcome_message === 'string'
    ? row.outcome_message.trim()
    : '';
  const direct = row?.snapshot_refresh;
  const nested = row?.metadata?.snapshot_refresh;
  const refresh = direct && typeof direct === 'object' ? direct
    : (nested && typeof nested === 'object' ? nested : null);
  const status = typeof refresh?.status === 'string' ? refresh.status.trim().toUpperCase() : '';
  return { message: raw, snapshot: raw || !SNAPSHOT_NOTE.has(status) ? '' : status };
}

/**
 * The outcomes actually present in a set of rows, with how many rows each
 * holds, in OUTCOME_META's own order.
 *
 * Only outcomes that occur are returned: History is filtered by clicking one,
 * and a chip that can only ever empty the list is a trap. Counts are over the
 * rows HANDED IN — the pages loaded so far — which is why the caller keeps
 * saying how many loaded rows a filter is hiding.
 */
export function outcomeFacets(rows) {
  const counts = new Map();
  const upstreams = new Map();
  (rows || []).forEach((row) => {
    const { key } = mutationOutcome(row);
    counts.set(key, (counts.get(key) || 0) + 1);
    upstreams.set(key, (upstreams.get(key) || new Set()).add(upstreamName(row)));
  });
  // One chip filters every channel, so it names the marketplace only when all
  // of its rows share one.
  const facetLabel = (key) => {
    if (key !== 'failed_at_amazon') return OUTCOME_META[key].label;
    const names = upstreams.get(key);
    if (names.size > 1) return 'Failed at the marketplace';
    return `Failed at ${[...names][0]}`;
  };
  return Object.keys(OUTCOME_META)
    .filter((key) => counts.has(key))
    .map((key) => ({ key, label: facetLabel(key), tone: OUTCOME_META[key].tone, count: counts.get(key) }));
}
